import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { ClipboardText, X, CaretLeft, CaretRight, Plus, PencilSimple, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { ATTENDANCE_STATUS, STATUS_COLORS, PLANNER_DAYS } from '../teacher/constants';

const DAY_LABELS = [...PLANNER_DAYS, 'Sabato', 'Domenica'];

const EMPTY_FORM = {
  id: null,
  teacher_id: '',
  student_id: '',
  lesson_id: '',
  date: '',
  start_time: '',
  end_time: '',
  status: '',
  notes: '',
};

export function AttendanceSection({ teachers, anno }) {
  const [data, setData] = useState({ records: [], total: 0, pages: 1, page: 1, status_counts: {} });
  const [loading, setLoading] = useState(false);
  const [teacherId, setTeacherId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [student, setStudent] = useState('');
  const [page, setPage] = useState(1);

  // Modal create/edit
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [studentOptions, setStudentOptions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const tid = teacherId && teacherId !== 'all' ? teacherId : undefined;
      const res = await api.getAdminAttendance({ teacher_id: tid, anno: anno || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined, student_name: student || undefined, page, limit: 50 });
      setData(res.data);
    } catch { toast.error('Errore nel caricamento presenze'); }
    finally { setLoading(false); }
  }, [teacherId, dateFrom, dateTo, student, page, anno]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [teacherId, dateFrom, dateTo, student]);

  // Carica lista alunni quando serve (per il select nel modal)
  useEffect(() => {
    if (!modalOpen || modalMode !== 'create') return;
    // Carica gli alunni collegati al docente selezionato
    (async () => {
      try {
        const res = await api.getAdminAllStudents({ anno, teacher_id: form.teacher_id });
        setStudentOptions(res.data || []);
      } catch { setStudentOptions([]); }
    })();
  }, [modalOpen, modalMode, anno, form.teacher_id]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await api.updateAdminAttendance(id, { status: newStatus });
      setData(prev => ({
        ...prev,
        records: prev.records.map(r => r.id === id ? { ...r, status: newStatus } : r)
      }));
    } catch { toast.error('Errore aggiornamento stato'); }
  };

  const openCreate = () => {
    // Il docente deve essere selezionato nel filtro
    if (!teacherId || teacherId === 'all') {
      toast.error('Seleziona prima un insegnante dal filtro in alto');
      return;
    }
    const yr = anno || new Date().getFullYear();
    setForm({ ...EMPTY_FORM, teacher_id: teacherId, date: `${yr}-09-01` });
    setModalMode('create');
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setForm({
      id: record.id,
      teacher_id: record.teacher_id || '',
      student_id: record.student_id || '',
      lesson_id: record.lesson_id || '',
      date: record.date || '',
      start_time: record.start_time || '',
      end_time: record.end_time || '',
      status: record.status || '',
      notes: record.notes || '',
    });
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`Eliminare la presenza di ${record.student_name} del ${new Date(record.date).toLocaleDateString('it-IT')}?`)) return;
    try {
      await api.deleteAdminAttendance(record.id);
      toast.success('Presenza eliminata');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore eliminazione'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.start_time) { toast.error('Data e ora inizio sono obbligatori'); return; }
    if (modalMode === 'create' && (!form.teacher_id || !form.student_id)) { toast.error('Seleziona docente e alunno'); return; }
    setSaving(true);
    try {
      if (modalMode === 'create') {
        await api.createAdminAttendance({
          teacher_id: form.teacher_id,
          student_id: form.student_id,
          lesson_id: form.lesson_id || '',
          date: form.date,
          start_time: form.start_time,
          end_time: form.end_time,
          status: form.status,
          notes: form.notes,
        });
        toast.success('Presenza creata');
      } else {
        await api.updateAdminAttendanceDetails(form.id, {
          date: form.date,
          start_time: form.start_time,
          end_time: form.end_time,
          status: form.status,
          notes: form.notes,
        });
        toast.success('Presenza aggiornata');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // Filtra alunni: già filtrati backend per teacher_id, mostra alunni con almeno una lezione dell'anno
  const filteredStudentsForTeacher = studentOptions;

  // Alunno selezionato e le sue lezioni (per mostrare quale lezione fa e pre-popolare orari)
  const selectedStudentData = studentOptions.find(s => s.id === form.student_id) || null;
  const studentLessons = (selectedStudentData?.lessons || []).filter(l =>
    (!form.teacher_id || l.teacher_id === form.teacher_id)
  );

  const applyLessonPreset = (lesson) => {
    if (!lesson) return;
    setForm(f => ({
      ...f,
      lesson_id: lesson.id || '',
      start_time: lesson.start_time || '',
      end_time: lesson.end_time || '',
    }));
  };

  // Auto-preseleziona la prima lezione quando l'alunno cambia
  useEffect(() => {
    if (modalMode !== 'create') return;
    if (studentLessons.length > 0 && !form.lesson_id) {
      applyLessonPreset(studentLessons[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.student_id, modalMode]);

  const selectedTeacherName = teachers.find(t => t.id === form.teacher_id)?.name || '';

  return (
    <>
      <div className="mb-6 animate-fade-in">
        <div className="flex items-center gap-3 flex-wrap mb-2">
          <h1 className="text-2xl sm:text-3xl font-bold font-['Work_Sans'] tracking-tight">Rilevazione Presenze</h1>
          {anno && (
            <span className="inline-flex items-center gap-1.5 bg-[#2D2A4A] text-white text-sm font-bold px-3 py-1.5 rounded-sm" data-testid="attendance-year-badge">
              <span className="text-[10px] uppercase tracking-wider text-white/70">Anno</span>
              <span>{anno} / {(anno + 1).toString().slice(-2)}</span>
            </span>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              if (!window.confirm('Ricalcola il campo "anno accademico" di TUTTI i record di presenza in base alla loro data. Utile se alcuni record risultano nell\'anno sbagliato. Continuare?')) return;
              try {
                const res = await api.fixAdminAttendanceAnno();
                toast.success(`Riallineamento completato · ${res.data.fixed} record corretti su ${res.data.scanned} scansionati`);
                load();
              } catch (err) { toast.error(err.response?.data?.detail || 'Errore riallineamento'); }
            }}
            className="ml-auto rounded-sm h-9"
            title="Ricalcola l'anno accademico di tutti i record in base alla data"
            data-testid="btn-fix-anno"
          >
            🔧 Correggi anno
          </Button>
          <Button
            size="sm"
            onClick={openCreate}
            className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm h-9 text-white"
            data-testid="btn-new-attendance"
          >
            <Plus size={16} weight="bold" className="mr-1" />Nuova Lezione
          </Button>
        </div>
        <p className="text-[#52525B] text-sm">Visualizza, crea e modifica le presenze. Clicca sullo stato per cambiarlo rapidamente. Cambia anno dalla sidebar.</p>
      </div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4 flex-wrap animate-fade-in animate-delay-1">
        <Select value={teacherId} onValueChange={setTeacherId}>
          <SelectTrigger className="w-full sm:w-[240px] rounded-sm h-9 text-sm bg-[#0A0A0A] text-white border-[#0A0A0A] font-medium [&>svg]:text-white" data-testid="admin-att-teacher-filter"><SelectValue placeholder="Tutti gli insegnanti" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli insegnanti</SelectItem>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name?.toUpperCase()} - {t.instrument}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex items-center gap-2 flex-wrap"><span className="text-sm text-[#52525B]">Dal</span><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-auto rounded-sm h-9 text-sm" data-testid="admin-att-date-from" /><span className="text-sm text-[#52525B]">al</span><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-auto rounded-sm h-9 text-sm" data-testid="admin-att-date-to" /></div>
        <Input type="text" placeholder="Cerca alunno..." value={student} onChange={(e) => setStudent(e.target.value)} className="w-full sm:w-[180px] rounded-sm h-9 text-sm uppercase" data-testid="admin-att-student-filter" />
        {(teacherId || dateFrom || dateTo || student) && <Button variant="ghost" size="sm" onClick={() => { setTeacherId(''); setDateFrom(''); setDateTo(''); setStudent(''); }} className="h-9 text-sm text-[#52525B]"><X size={14} className="mr-1" />Pulisci</Button>}
        <div className="text-sm text-[#52525B] font-medium ml-auto" data-testid="admin-att-total">{data.total} {data.total === 1 ? 'presenza' : 'presenze'}</div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4 animate-fade-in animate-delay-1">
        {ATTENDANCE_STATUS.map(status => {
          const count = data.status_counts[status] || 0;
          return (<div key={status} className={`px-2 py-2 rounded-sm border text-center ${STATUS_COLORS[status]}`}><div className="text-lg font-bold font-['Work_Sans'] leading-tight">{count}</div><div className="text-[9px] font-semibold uppercase tracking-wider leading-tight">{status}</div></div>);
        })}
        <div className="px-2 py-2 rounded-sm border text-center bg-gray-50 text-gray-600 border-gray-200"><div className="text-lg font-bold font-['Work_Sans'] leading-tight">{data.status_counts[''] || data.status_counts['0'] || 0}</div><div className="text-[9px] font-semibold uppercase tracking-wider leading-tight">Vuote</div></div>
      </div>
      <div className="animate-fade-in animate-delay-2 overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
        {loading ? <div className="text-center py-12 text-[#52525B]">Caricamento...</div> : data.records.length === 0 ? (
          <div className="text-center py-16 bg-white border border-[#E4E4E7] rounded-sm"><ClipboardText size={48} className="mx-auto text-[#E4E4E7] mb-4" /><p className="text-[#52525B]">Nessuna presenza registrata</p></div>
        ) : (
          <table className="data-table" data-testid="admin-attendance-table">
            <thead><tr><th>Data</th><th>Insegnante</th><th>Alunno</th><th>Orario</th><th>Stato</th><th>Azioni</th></tr></thead>
            <tbody>{data.records.map((r) => {
              const dateObj = new Date(r.date);
              const dateStr = dateObj.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short', year: '2-digit' });
              return (<tr key={r.id} data-testid={`att-row-${r.id}`}>
                <td className="whitespace-nowrap text-sm capitalize">{dateStr}</td>
                <td className="text-sm">{r.teacher_name}</td>
                <td className="text-sm font-medium uppercase">{r.student_name}</td>
                <td className="text-sm text-[#52525B]">{r.start_time || '-'} - {r.end_time || '-'}</td>
                <td>
                  <select
                    value={r.status || ''}
                    onChange={(e) => handleStatusChange(r.id, e.target.value)}
                    className={`text-xs font-medium px-2 py-1 rounded border cursor-pointer outline-none ${STATUS_COLORS[r.status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}
                    data-testid={`att-status-${r.id}`}
                  >
                    <option value="">-- Vuoto --</option>
                    {ATTENDANCE_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
                    <option value="0">0</option>
                  </select>
                </td>
                <td>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEdit(r)}
                      className="p-1.5 hover:bg-[#F4F4F5] rounded transition-colors text-[#2D2A4A]"
                      title="Modifica"
                      data-testid={`att-edit-${r.id}`}
                    >
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    <button
                      onClick={() => handleDelete(r)}
                      className="p-1.5 hover:bg-red-50 text-red-600 rounded transition-colors"
                      title="Elimina"
                      data-testid={`att-delete-${r.id}`}
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </td>
              </tr>);
            })}</tbody>
          </table>
        )}
      </div>
      {data.pages > 1 && (
        <div className="flex items-center justify-between mt-4 mb-16">
          <div className="text-sm text-[#52525B]">Pagina {page} di {data.pages}</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-sm"><CaretLeft size={14} className="mr-1" />Precedente</Button>
            <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)} className="rounded-sm">Successiva<CaretRight size={14} className="ml-1" /></Button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="attendance-modal">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans']">{modalMode === 'create' ? 'Nuova Lezione (Presenza)' : 'Modifica Presenza'}</DialogTitle>
            <DialogDescription>
              {modalMode === 'create'
                ? 'Crea manualmente un record di presenza per una singola data/orario.'
                : 'Modifica data, orario, stato e note del record di presenza.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            {modalMode === 'create' && (
              <>
                <div className="bg-[#F4F4F5] border border-[#E4E4E7] rounded-sm px-3 py-2 text-xs">
                  <span className="text-[#52525B] uppercase tracking-wider text-[10px]">Insegnante</span>
                  <div className="font-semibold uppercase text-[#2D2A4A]">{selectedTeacherName}</div>
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Alunno <span className="text-red-500">*</span></Label>
                  <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v, lesson_id: '', start_time: '', end_time: '' })}>
                    <SelectTrigger className="rounded-sm" data-testid="modal-student-select">
                      <SelectValue placeholder={filteredStudentsForTeacher.length === 0 ? 'Nessun alunno collegato a questo docente' : 'Seleziona alunno'} />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredStudentsForTeacher.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name?.toUpperCase()}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Info lezione dell'alunno (dalla lista alunni) */}
                {form.student_id && studentLessons.length > 0 && (
                  <div className="bg-[#EEF2FF] border border-[#C7D2FE] rounded-sm p-3 text-xs space-y-1.5" data-testid="student-lessons-info">
                    <div className="text-[10px] uppercase tracking-wider text-[#4338CA] font-semibold">
                      Lezioni collegate ({studentLessons.length})
                    </div>
                    <div className="space-y-1">
                      {studentLessons.map(l => (
                        <label
                          key={l.id}
                          className={`flex items-center gap-2 p-1.5 rounded cursor-pointer text-[#2D2A4A] transition-colors ${form.lesson_id === l.id ? 'bg-white border border-[#4338CA]' : 'hover:bg-white/50'}`}
                          data-testid={`lesson-option-${l.id}`}
                        >
                          <input
                            type="radio"
                            name="lesson_preset"
                            checked={form.lesson_id === l.id}
                            onChange={() => applyLessonPreset(l)}
                          />
                          <span className="font-semibold">{DAY_LABELS[l.day] || '?'}</span>
                          <span className="font-['Work_Sans']">{l.start_time} - {l.end_time}</span>
                          {l.is_prova && <span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded uppercase">Prova</span>}
                        </label>
                      ))}
                    </div>
                    <p className="text-[10px] text-[#4338CA] italic pt-1">Selezionando una lezione, gli orari verranno pre-compilati sotto (puoi comunque modificarli).</p>
                  </div>
                )}
                {form.student_id && studentLessons.length === 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-sm p-2 text-xs text-amber-800">
                    ⚠️ Questo alunno non ha lezioni collegate con questo docente. Puoi comunque creare la presenza specificando data e orari manualmente.
                  </div>
                )}
              </>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className="label-overline">Data <span className="text-red-500">*</span></Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm" data-testid="modal-date-input" required disabled={modalMode === 'create' && !form.student_id} />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Dalle <span className="text-red-500">*</span></Label>
                <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="rounded-sm" data-testid="modal-start-input" required disabled={modalMode === 'create' && !form.student_id} />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Alle</Label>
                <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="rounded-sm" data-testid="modal-end-input" disabled={modalMode === 'create' && !form.student_id} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Stato</Label>
              <Select value={form.status || '__empty__'} onValueChange={(v) => setForm({ ...form, status: v === '__empty__' ? '' : v })}>
                <SelectTrigger className="rounded-sm" data-testid="modal-status-select"><SelectValue placeholder="Stato" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__empty__">-- Vuoto --</SelectItem>
                  {ATTENDANCE_STATUS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Annotazioni</Label>
              <Input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Note opzionali..." className="rounded-sm" data-testid="modal-notes-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)} className="rounded-sm">Annulla</Button>
              <Button type="submit" disabled={saving || (modalMode === 'create' && !form.student_id)} className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm" data-testid="modal-submit-btn">
                {saving ? 'Salvataggio...' : (modalMode === 'create' ? 'Crea Presenza' : 'Salva Modifiche')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
